import ApiService from './api-service';
import download from '../util/download';
import InvoiceService from './invoice-service';
import CustomerService from './customer-service';

const ReportsService = {
  async getReportGeneratorReports(shortCode) {
    const body = {
      query: `query {
        reportGeneratorReports {
          id
          name
          description
        }
      }`,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.reportGeneratorReports;
  },

  async getReportGeneratorReportOptions(shortCode, reportId) {
    const body = {
      query: `query($reportId: String!) {
        reportGeneratorReportOptions(reportId: $reportId) {
          reportId
          reportName
          defaults {
            sheets
            fields
          }
          filters {
            id
            label
            type
            description
            multiple
            options
            default
          }
          fields {
            id
            label
            description
            required
            category
          }
          coreFields {
            id
            label
            description
          }
        }
      }`,
      variables: {
        reportId,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.reportGeneratorReportOptions;
  },

  async getReportGeneratorFilterOptions(shortCode, kind) {
    const body = {
      query: `query($kind: String!) {
        reportGeneratorFilterOptions(kind: $kind) {
          value
          label
        }
      }`,
      variables: {
        kind,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.reportGeneratorFilterOptions;
  },

  async generateReportGeneratorReport(shortCode, { reportId, config }) {
    const body = {
      query: `mutation($reportId: String!, $config: ReportGeneratorGenerateConfigInput!) {
        generateReportGeneratorReport(reportId: $reportId, config: $config) {
          filename
          mimeType
          base64
        }
      }`,
      variables: {
        reportId,
        config,
      },
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.generateReportGeneratorReport;
  },

  async downloadCustomerReport(shortCode, filter) {
    const body = {
      query: `mutation generateCustomerReport($filter: ContactFilter) {
        customerReport(filter: $filter)
      }`,
      variables: {
        filter: CustomerService.getCustomerFilter(filter),
      },
    };
    const { customerReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(customerReport);
    download(res.data, 'customers.xlsx');
  },

  async downloadInvoiceReport(shortCode, filter) {
    const body = {
      query: `mutation generateInvoiceReport($filter: InvoiceFilter) {
      invoiceReport(filter: $filter)
    }`,
      variables: {
        filter: InvoiceService.getInvoiceFilter(filter),
      },
    };
    const { invoiceReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(invoiceReport);
    download(res.data, 'invoices.xlsx');
  },

  async downloadInvoiceHistoryReport(shortCode, invoiceId, invoice, filter = {}) {
    const body = {
      query: `mutation generateInvoiceHistoryReport($filter: String!, $invoiceId: String!) {
      invoiceHistoryReport(filter: $filter, invoiceId: $invoiceId)
    }`,
      variables: {
        filter,
        invoiceId: invoiceId,
      },
    };

    const { invoiceHistoryReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(invoiceHistoryReport);
    download(res.data, `${invoice.InvoiceNumber}-history.xlsx`);
  },

  async downloadCustomerHistoryReport(shortCode, customerId, customer, filter = {}) {
    const body = {
      query: `mutation generateCustomerHistoryReport($filter: String!, $customerId: String!) {
      customerHistoryReport(filter: $filter, customerId: $customerId)
    }`,
      variables: {
        filter,
        customerId: customerId,
      },
    };

    const { customerHistoryReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(customerHistoryReport);
    download(res.data, `${customer.Name}-history.xlsx`);
  },

  async getCustomers({ shortCode, sort, skip, limit, search }) {
    const body = {
      query: `
        query($skip: Int, $limit: Int, $sort: String, $filter: ContactFilter) {
          organisation {
            contacts(skip: $skip, limit: $limit, sort: $sort, filter: $filter) {
              totalCount
              nodes {
                _id
                ContactID
                Name
                Balances {
                  AccountsReceivable {
                    Outstanding
                    Overdue
                  }
                }
                threshold {
                  creditLimit
                  default
                }
                riskReport {
                  totalDebtPercentage
                  creditLimitPercentage
                }
                customerReport {
                  oldestDebt
                  daysToPay
                  lastPaymentDate
                  replyRate
                  lastReplyDate
                }
              }
            }
          }
        }
      `,
      variables: {
        sort,
        skip,
        limit,
        filter: {
          name: search,
          archived: false,
        },
      },
    };
    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation.contacts;
  },

  async downloadRiskReport(shortCode) {
    const body = {
      query: `mutation {
        customersRiskReport
      }`,
    };
    const { customersRiskReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(customersRiskReport);
    download(res.data, 'RiskReport.xlsx');
  },

  async downloadCustomerReportReport(shortCode) {
    const body = {
      query: `mutation {
        customersReport
      }`,
    };
    const { customersReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(customersReport);
    download(res.data, 'CustomerReport.xlsx');
  },

  async getOrganisation(shortCode) {
    const body = {
      query: `query {
        organisation {
          reports {
            amountOutstandingOverdue {
              createdAt
              months {
                from
                to
                outstanding
                overdue
              }
              weeks {
                from
                to
                outstanding
                overdue
              }
            }
            amountPaid {
            createdAt
              months {
                from
                to
                paid
              }
              weeks {
                from
                to
                paid
              }
            }
            amountChased {
            createdAt
              months {
                from
                to
                chased
              }
              weeks {
                from
                to
                chased
              }
            }
            chasing {
              currency
              weeks {
                notChasing
                chasing
                inDispute
                inCollections
                paid
                lastUpdatedAt
              }
              months {
                notChasing
                chasing
                inDispute
                inCollections
                paid
                lastUpdatedAt
              }
            }
          }
        }
      }`,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation.reports;
  },

  async downloadOrganisationReport(shortCode) {
    const body = {
      query: `mutation {
        debtorReport
      }`,
    };
    const { debtorReport } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(debtorReport);
    download(res.data, 'DebtorReport.xlsx');
  },

  async downloadRevenueForecast(shortCode, startDate) {
    const body = {
      query: `query($startDate: Date) {
        organisation {
          generateRevenueForecastReport(startDate: $startDate)
        }
      }`,
      variables: {
        startDate,
      },
    };

    const { organisation } = await ApiService.orgEndpoint(shortCode).post('/', body);
    const res = await ApiService.fileDownload(`/${organisation.generateRevenueForecastReport}`);
    download(res.data, 'RevenueReport.xlsx');
  },

  async getTopDebtors(shortCode) {
    const body = {
      query: `query {
        organisation {
          reports {
            topDebtors {
              _id {
                debtor
              }
              agingClusters {
                value
                group
                dynamicColumns
              }
              totalDue
              lastUpdatedAt
            }
          }
        }
      }`,
    };

    const data = await ApiService.orgEndpoint(shortCode).post('/', body);
    return data.organisation.reports.topDebtors;
  },
};

export default ReportsService;
